/* iter174 — Admin Promo Links CRUD.
   Lives at /admin/promos under System Settings. Lets admins create + edit
   the promo destinations behind printed QR codes. The QR points at the
   permanent NexFlow URL (/offer or /promo/{slug}); this page edits what
   the URL DOES — landing card or 302 redirect.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import { Link as LinkIcon, Plus, Trash2, Pencil, ExternalLink, Copy, Eye, History, Users, Calendar, ListChecks } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import PromoCampaignsTab from './PromoCampaignsTab';
import PromoEntriesDrawer from './PromoEntriesDrawer';

const NEED_LABELS = {
  plumbing_repair: 'Plumbing Repair',
  drain_cleaning: 'Drain Cleaning',
  water_heater: 'Water Heater',
  toilet: 'Toilet',
  faucet_sink: 'Faucet / Sink',
  sump_pump: 'Sump Pump',
  sewer_main_line: 'Sewer / Main Line',
  water_line: 'Water Line',
  gas_line: 'Gas Line',
  remodel_rough_in: 'Remodel / Rough-In',
  membership_maintenance: 'Membership / Maintenance',
  commercial_plumbing: 'Commercial Plumbing',
  property_management: 'Property Management',
  other: 'Other',
};
const URGENCY_LABELS = {
  emergency_today: 'Emergency / Today',
  this_week: 'This Week',
  flexible: 'Flexible',
  just_question: 'Just a Question',
};
const CONTACT_LABELS = { call: 'Call', text: 'Text', email: 'Email' };
const LEAD_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'booked', label: 'Booked' },
  { value: 'closed', label: 'Closed' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

const blankPromo = () => ({
  name: '',
  slug: '',
  active: true,
  destination_type: 'landing',
  redirect_url: '',
  headline: 'Claim Today\'s NexFlow Offer',
  offer_text: 'Scan this QR code to view current NexFlow Plumbing & Drain Services promotions.',
  call_number: '614-285-4509',
  cta_label: 'Request Service',
  cta_url: '/customer/service-request',
  notes: '',
});

const PromoLinks = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // { ...promo, _isNew }
  // iter187 — Rich "View Entries" drawer state (separate from legacy leads panel).
  const [entriesFor, setEntriesFor] = useState(null); // { id, name }
  const [visits, setVisits] = useState({ open: false, rows: [], slug: '' });
  const [leadsPanel, setLeadsPanel] = useState({ open: false, rows: [], slug: '', promoId: '', loading: false });
  const [leadDetail, setLeadDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promos`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load promo links');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ ...blankPromo(), _isNew: true });
  const openEdit = (promo) => setEditing({ ...promo });

  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name,
      slug: (editing.slug || '').toLowerCase().trim(),
      active: !!editing.active,
      destination_type: editing.destination_type || 'landing',
      redirect_url: editing.redirect_url || '',
      headline: editing.headline || '',
      offer_text: editing.offer_text || '',
      call_number: editing.call_number || '',
      cta_label: editing.cta_label || '',
      cta_url: editing.cta_url || '',
      notes: editing.notes || '',
    };
    if (!payload.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!payload.slug) {
      toast.error('Slug is required (e.g. "offer" or "yard-sign")');
      return;
    }
    // iter174 fix — Reject invalid slugs explicitly instead of silently
    // sanitizing them. Keeps printed-QR URLs intentional and prevents the
    // admin from accidentally creating a URL they didn't mean to type.
    if (!/^[a-z0-9][a-z0-9-]*$/.test(payload.slug)) {
      toast.error('Slug must be lowercase letters, numbers, and dashes only (e.g. "offer", "yard-sign")');
      return;
    }
    setSaving(true);
    try {
      if (editing._isNew) {
        await axios.post(`${API}/admin/promos`, payload, { withCredentials: true });
        toast.success('Promo created');
      } else {
        await axios.put(`${API}/admin/promos/${editing.id}`, payload, { withCredentials: true });
        toast.success('Promo saved');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (promo) => {
    if (!window.confirm(`Delete promo "${promo.name}" (slug: ${promo.slug})?\n\nThe printed QR code pointing at this URL will stop working.`)) return;
    try {
      await axios.delete(`${API}/admin/promos/${promo.id}`, { withCredentials: true });
      toast.success('Promo deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const openVisits = async (promo) => {
    try {
      const { data } = await axios.get(`${API}/admin/promos/${promo.id}/visits`, { withCredentials: true });
      setVisits({ open: true, rows: Array.isArray(data) ? data : [], slug: promo.slug });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load visit log');
    }
  };

  const openLeads = async (promo) => {
    setLeadsPanel({ open: true, rows: [], slug: promo.slug, promoId: promo.id, loading: true });
    try {
      const { data } = await axios.get(`${API}/admin/promos/${promo.id}/leads`, { withCredentials: true });
      setLeadsPanel({ open: true, rows: Array.isArray(data) ? data : [], slug: promo.slug, promoId: promo.id, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load submissions');
      setLeadsPanel((s) => ({ ...s, loading: false }));
    }
  };

  const reloadLeads = async (promoId) => {
    try {
      const { data } = await axios.get(`${API}/admin/promos/${promoId}/leads`, { withCredentials: true });
      setLeadsPanel((s) => ({ ...s, rows: Array.isArray(data) ? data : [] }));
    } catch (e) {
      // non-fatal
    }
  };

  const setLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/admin/promo-leads/${leadId}/status`, { status }, { withCredentials: true });
      toast.success('Status updated');
      setLeadDetail((d) => (d ? { ...d, status } : d));
      if (leadsPanel.promoId) reloadLeads(leadsPanel.promoId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    }
  };

  const copyUrl = async (slug) => {
    const url = `${FRONTEND_ORIGIN}/promo/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${url}`);
    } catch {
      toast.message(url);
    }
  };

  return (
    <div data-testid="promo-links-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white flex items-center gap-2">
          <LinkIcon className="w-6 h-6 text-[#D4AF37]" /> Promo Links & Campaigns
        </h2>
        <p className="text-[#A3A3A3] max-w-3xl text-sm mt-1">
          One permanent QR code can rotate through multiple scheduled promotions.
          Manage the schedule in <strong className="text-white">Campaigns</strong>; manage the underlying
          permanent URLs in <strong className="text-white">URLs</strong>.
        </p>
      </div>

      <Tabs defaultValue="campaigns" className="w-full">
        <TabsList data-testid="promo-tabs" className="bg-[#141414] border border-[#262626]">
          <TabsTrigger data-testid="promo-tab-campaigns" value="campaigns" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <Calendar className="w-4 h-4 mr-1.5" /> Campaigns
          </TabsTrigger>
          <TabsTrigger data-testid="promo-tab-urls" value="urls" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <LinkIcon className="w-4 h-4 mr-1.5" /> URLs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="mt-5">
          <PromoCampaignsTab />
        </TabsContent>

        <TabsContent value="urls" className="mt-5 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-['Outfit'] text-2xl font-bold text-white flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-[#D4AF37]" /> Permanent Promo URLs
          </h3>
          <p className="text-[#A3A3A3] max-w-3xl text-sm mt-1">
            Permanent NexFlow URLs you can print on feather flags, yard signs, door hangers, and business cards.
            The QR code points at <code className="text-[#D4AF37]">/promo/&lt;slug&gt;</code> — what that URL DOES (landing card or 302 redirect)
            can change anytime without reprinting.
          </p>
        </div>
        <Button
          data-testid="promo-add"
          onClick={openCreate}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Promo Link
        </Button>
      </div>

      {loading ? (
        <p className="text-[#A3A3A3]">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="promo-empty" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-[#A3A3A3]">
          No promos yet. Click <strong className="text-white">New Promo Link</strong> to create your first one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`promo-row-${p.slug}`}
              className="bg-[#141414] border border-[#262626] rounded-md p-4 flex flex-wrap items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-bold">{p.name}</p>
                  {p.active ? (
                    <span className="text-[10px] uppercase tracking-wider text-green-300 bg-green-500/15 border border-green-500/40 px-1.5 py-0.5 rounded">Active</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-[#737373] bg-[#262626] border border-[#3a3a3a] px-1.5 py-0.5 rounded">Inactive</span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                    {p.destination_type === 'redirect' ? 'Redirect' : 'Landing'}
                  </span>
                </div>
                <p className="text-[#A3A3A3] text-xs font-mono mt-1 truncate">
                  {FRONTEND_ORIGIN}/promo/{p.slug}
                </p>
                <p className="text-[#525252] text-xs mt-1">
                  {p.visit_count || 0} visits
                  {typeof p.lead_count === 'number' && (<> · {p.lead_count} leads</>)}
                  {p.last_visit_at && <> · last {new Date(p.last_visit_at).toLocaleString()}</>}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Button data-testid={`promo-copy-${p.slug}`} size="sm" variant="outline" onClick={() => copyUrl(p.slug)} className="border-[#262626] text-white hover:bg-[#262626]" title="Copy URL">
                  <Copy className="w-4 h-4" />
                </Button>
                <a
                  href={`/promo/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`promo-open-${p.slug}`}
                  className="inline-flex items-center gap-1 px-2 py-1.5 border border-[#262626] text-white hover:bg-[#262626] rounded text-sm"
                  title="Open public page"
                >
                  <Eye className="w-4 h-4" />
                </a>
                <Button data-testid={`promo-entries-${p.slug}`} size="sm" variant="outline" onClick={() => setEntriesFor({ id: p.id, name: p.name || p.slug })} className="border-[#3a3a3a] text-white hover:bg-[#262626]" title="View campaign entries with source, consent, SMS, duplicates + CSV export">
                  <ListChecks className="w-4 h-4" />
                </Button>
                <Button data-testid={`promo-leads-${p.slug}`} size="sm" variant="outline" onClick={() => openLeads(p)} className="border-[#262626] text-white hover:bg-[#262626]" title="Submissions / Leads">
                  <Users className="w-4 h-4" />
                </Button>
                <Button data-testid={`promo-visits-${p.slug}`} size="sm" variant="outline" onClick={() => openVisits(p)} className="border-[#262626] text-white hover:bg-[#262626]" title="Visit log">
                  <History className="w-4 h-4" />
                </Button>
                <Button data-testid={`promo-edit-${p.slug}`} size="sm" onClick={() => openEdit(p)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button data-testid={`promo-delete-${p.slug}`} size="sm" variant="outline" onClick={() => remove(p)} className="border-red-500/40 text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor sheet */}
      <Sheet open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <SheetContent
          side="right"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto"
          data-testid="promo-edit-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-[#D4AF37]" />
              {editing?._isNew ? 'New Promo Link' : `Edit "${editing?.name || ''}"`}
            </SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              The slug becomes the permanent URL. Once you print a QR pointing at <code className="text-[#D4AF37]">/promo/&lt;slug&gt;</code>,
              don&apos;t change the slug — change the destination instead.
            </SheetDescription>
          </SheetHeader>

          {editing && (
            <div className="mt-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Name *</Label>
                  <Input
                    data-testid="promo-name"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Feather Flag"
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Slug *</Label>
                  <Input
                    data-testid="promo-slug"
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="offer"
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1 font-mono"
                    disabled={!editing._isNew && editing.slug === 'offer'}
                  />
                  {editing.slug && (
                    <p className="text-[#737373] text-[11px] font-mono mt-1 break-all">
                      → {FRONTEND_ORIGIN}/promo/{editing.slug}
                    </p>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3">
                <Switch
                  data-testid="promo-active"
                  checked={!!editing.active}
                  onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                />
                <div>
                  <p className="text-white text-sm font-bold">Active</p>
                  <p className="text-[#A3A3A3] text-[11px]">If off, visitors see a safe &quot;Not currently available&quot; fallback page.</p>
                </div>
              </label>

              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Destination</Label>
                <Select value={editing.destination_type} onValueChange={(v) => setEditing({ ...editing, destination_type: v })}>
                  <SelectTrigger data-testid="promo-destination-type" className="bg-[#0A0A0A] border-[#262626] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="landing" className="text-white">Show NexFlow landing page</SelectItem>
                    <SelectItem value="redirect" className="text-white">Redirect to external URL (302)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editing.destination_type === 'redirect' && (
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Redirect URL *</Label>
                  <Input
                    data-testid="promo-redirect-url"
                    value={editing.redirect_url || ''}
                    onChange={(e) => setEditing({ ...editing, redirect_url: e.target.value })}
                    placeholder="https://example.com/landing"
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
              )}

              {editing.destination_type !== 'redirect' && (
                <>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Headline</Label>
                    <Input
                      data-testid="promo-headline-field"
                      value={editing.headline || ''}
                      onChange={(e) => setEditing({ ...editing, headline: e.target.value })}
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Offer Text</Label>
                    <Textarea
                      data-testid="promo-offer-text-field"
                      rows={3}
                      value={editing.offer_text || ''}
                      onChange={(e) => setEditing({ ...editing, offer_text: e.target.value })}
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Call Number</Label>
                      <Input
                        data-testid="promo-call-number"
                        value={editing.call_number || ''}
                        onChange={(e) => setEditing({ ...editing, call_number: e.target.value })}
                        placeholder="614-285-4509"
                        className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">CTA Label</Label>
                      <Input
                        data-testid="promo-cta-label"
                        value={editing.cta_label || ''}
                        onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })}
                        placeholder="Request Service"
                        className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">CTA URL</Label>
                    <Input
                      data-testid="promo-cta-url"
                      value={editing.cta_url || ''}
                      onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })}
                      placeholder="/customer/service-request"
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1 font-mono"
                    />
                  </div>
                </>
              )}

              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Internal Notes</Label>
                <Textarea
                  data-testid="promo-notes"
                  rows={2}
                  value={editing.notes || ''}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="What's this QR printed on? Where?"
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                />
              </div>

              <div className="pt-2 flex items-center gap-2">
                <Button
                  data-testid="promo-save"
                  onClick={save}
                  disabled={saving}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
                >
                  {saving ? 'Saving…' : 'Save Promo'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditing(null)}
                  className="border-[#262626] text-white hover:bg-[#262626]"
                >
                  Cancel
                </Button>
                {!editing._isNew && editing.slug && (
                  <a
                    href={`/promo/${editing.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="promo-preview"
                    className="ml-auto inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-sm font-bold"
                  >
                    Preview <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Visit log sheet */}
      <Sheet open={visits.open} onOpenChange={(o) => setVisits((v) => ({ ...v, open: o }))}>
        <SheetContent
          side="right"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-lg overflow-y-auto"
          data-testid="promo-visits-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-white">Visit Log · {visits.slug}</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              Last 200 visits. IP / user agent / referrer captured when available.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-1.5">
            {visits.rows.length === 0 ? (
              <p className="text-[#525252] text-sm italic">No visits yet.</p>
            ) : (
              visits.rows.map((v) => (
                <div key={v.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-xs">
                  <p className="text-white">{v.ts ? new Date(v.ts).toLocaleString() : '—'}</p>
                  <p className="text-[#A3A3A3]">IP: {v.ip || '—'}</p>
                  <p className="text-[#737373] truncate" title={v.user_agent}>{v.user_agent || '—'}</p>
                  {v.referrer && <p className="text-[#737373] truncate" title={v.referrer}>Ref: {v.referrer}</p>}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Submissions / Leads sheet */}
      <Sheet open={leadsPanel.open} onOpenChange={(o) => setLeadsPanel((s) => ({ ...s, open: o }))}>
        <SheetContent
          side="right"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto"
          data-testid="promo-leads-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-white">Submissions · {leadsPanel.slug}</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              Customers who filled out the intake form on this promo page. Click a row for details and to change status.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-2">
            {leadsPanel.loading ? (
              <p className="text-[#A3A3A3] text-sm">Loading…</p>
            ) : leadsPanel.rows.length === 0 ? (
              <p data-testid="promo-leads-empty" className="text-[#525252] text-sm italic">No submissions yet.</p>
            ) : (
              leadsPanel.rows.map((l) => (
                <button
                  key={l.id}
                  data-testid={`promo-lead-row-${l.id}`}
                  onClick={() => setLeadDetail(l)}
                  className="w-full text-left bg-[#0A0A0A] border border-[#262626] rounded p-3 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-white font-bold">{l.customer_name || '—'}</p>
                      <p className="text-[#A3A3A3] text-xs mt-0.5">{l.phone} {l.email && <>· {l.email}</>}</p>
                      <p className="text-[#737373] text-xs mt-0.5 truncate">
                        {[l.address, l.city, l.zip].filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {(l.urgency === 'emergency_today') && (
                        <span className="text-[10px] uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-500/40 px-1.5 py-0.5 rounded">Urgent</span>
                      )}
                      <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-1.5 py-0.5 rounded">
                        {l.status || 'new'}
                      </span>
                    </div>
                  </div>
                  <div className="text-[#737373] text-[11px] mt-2 flex flex-wrap gap-2">
                    {(l.needs || []).map((n) => (
                      <span key={n} className="bg-[#141414] border border-[#262626] px-1.5 py-0.5 rounded">{NEED_LABELS[n] || n}</span>
                    ))}
                    {l.urgency && <span className="bg-[#141414] border border-[#262626] px-1.5 py-0.5 rounded">{URGENCY_LABELS[l.urgency] || l.urgency}</span>}
                    {l.preferred_contact && <span className="bg-[#141414] border border-[#262626] px-1.5 py-0.5 rounded">Prefers {CONTACT_LABELS[l.preferred_contact]}</span>}
                    <span className="ml-auto">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Lead detail / status update sheet */}
      <Sheet open={!!leadDetail} onOpenChange={(o) => { if (!o) setLeadDetail(null); }}>
        <SheetContent
          side="right"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-lg overflow-y-auto"
          data-testid="promo-lead-detail"
        >
          <SheetHeader>
            <SheetTitle className="text-white">Lead Detail</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              {leadDetail?.matched_existing_customer ? 'Matched an existing customer.' : 'Created a new customer record.'}
            </SheetDescription>
          </SheetHeader>
          {leadDetail && (
            <div className="mt-5 space-y-4 text-sm">
              <div>
                <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Name</p>
                <p className="text-white font-bold">{leadDetail.customer_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Phone</p>
                  <p className="text-white">{leadDetail.phone}</p>
                </div>
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Email</p>
                  <p className="text-white">{leadDetail.email || '—'}</p>
                </div>
              </div>
              <div>
                <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Service Address</p>
                <p className="text-white">{[leadDetail.address, leadDetail.city, leadDetail.zip].filter(Boolean).join(', ')}</p>
              </div>
              {(leadDetail.needs && leadDetail.needs.length > 0) && (
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Need</p>
                  <p className="text-white">{leadDetail.needs.map((n) => NEED_LABELS[n] || n).join(', ')}</p>
                </div>
              )}
              {leadDetail.urgency && (
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Urgency</p>
                  <p className="text-white">{URGENCY_LABELS[leadDetail.urgency] || leadDetail.urgency}</p>
                </div>
              )}
              {leadDetail.preferred_contact && (
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Preferred Contact</p>
                  <p className="text-white">{CONTACT_LABELS[leadDetail.preferred_contact]}</p>
                </div>
              )}
              {leadDetail.notes && (
                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Notes</p>
                  <p className="text-white whitespace-pre-wrap">{leadDetail.notes}</p>
                </div>
              )}
              <div>
                <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Submitted</p>
                <p className="text-white">{leadDetail.created_at ? new Date(leadDetail.created_at).toLocaleString() : '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Status</Label>
                <Select value={leadDetail.status || 'new'} onValueChange={(v) => setLeadStatus(leadDetail.id, v)}>
                  <SelectTrigger data-testid="lead-status-select" className="bg-[#0A0A0A] border-[#262626] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {LEAD_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="text-white">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
        </TabsContent>
      </Tabs>

      {/* iter187 — Rich Entries drawer (source, consent, SMS, duplicates, CSV). */}
      <PromoEntriesDrawer
        open={!!entriesFor}
        onOpenChange={(o) => { if (!o) setEntriesFor(null); }}
        scope="promo"
        id={entriesFor?.id}
        name={entriesFor?.name}
      />
    </div>
  );
};

export default PromoLinks;
